import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, tl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  LayoutDashboard, Users, Briefcase, Wallet, ShieldAlert, Settings,
  FileText, Flag, Loader2, Home, Image as ImageIcon, Check, X, Rocket, Trash2, Plus, Ban, Pencil, ScrollText, Eye,
} from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;
import { toast } from "sonner";

const NAV = [
  { id: "dashboard", label: "Genel Bakış", icon: LayoutDashboard },
  { id: "users", label: "Kullanıcılar", icon: Users },
  { id: "jobs", label: "İlanlar", icon: Briefcase },
  { id: "transactions", label: "İşlemler", icon: Wallet },
  { id: "moderation", label: "Moderasyon", icon: ShieldAlert },
  { id: "reports", label: "Şikayetler", icon: Flag },
  { id: "chatbans", label: "Sohbet Banları", icon: Ban },
  { id: "audit", label: "İşlem Günlüğü", icon: ScrollText },
  { id: "images", label: "Görsel Onayları", icon: ImageIcon },
  { id: "boost", label: "Boost & Vitrin", icon: Rocket },
  { id: "banned", label: "Yasaklı Kelimeler", icon: Ban },
  { id: "blog", label: "Blog", icon: FileText },
  { id: "config", label: "Fiyat & Ayarlar", icon: Settings },
];

export default function Admin() {
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("dashboard");
  const [audit, setAudit] = useState([]);
  const [chatBans, setChatBans] = useState([]);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState(null);
  const [images, setImages] = useState([]);
  const [imgStatus, setImgStatus] = useState("pending");
  const [pkgs, setPkgs] = useState([]);
  const [newPkg, setNewPkg] = useState({ duration_hours: "", normal_price: "", sale_price: "" });
  const [bw, setBw] = useState([]);
  const [bwInput, setBwInput] = useState("");
  const [search, setSearch] = useState("");

  const loadBw = () => api.get("/admin/banned-words").then(({ data }) => setBw(data)).catch(() => {});
  useEffect(() => { if (section === "banned") loadBw(); }, [section]);
  useEffect(() => { setSearch(""); }, [section]);
  const addBw = async () => {
    if (!bwInput.trim()) return;
    try { await api.post("/admin/banned-words", { word: bwInput.trim() }); setBwInput(""); loadBw(); toast.success("Kelime eklendi"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delBw = async (id) => { try { await api.delete(`/admin/banned-words/${id}`); loadBw(); } catch (e) { toast.error(apiError(e)); } };
  const adjustBalance = async (uid, balance) => {
    const raw = window.prompt(`${balance === "bonus" ? "Bonus" : "Gerçek"} bakiye değişimi (eksi için - kullanın):`, "50");
    if (raw === null) return;
    const amount = Number(raw);
    if (!amount) return;
    const reason = window.prompt("Müdahale gerekçesi (Destek Talep No / Açıklama) — zorunlu:", "");
    if (!reason || !reason.trim()) { toast.error("Gerekçe zorunludur"); return; }
    try {
      await api.post("/admin/wallet/adjust", { user_id: uid, amount, type: "adjustment", description: "Admin bakiye düzenleme", balance, reason });
      toast.success("Bakiye güncellendi");
      api.get("/admin/users").then(({ data }) => setRows(data));
    } catch (e) { toast.error(apiError(e)); }
  };

  const impersonate = async (r) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${r.id}`);
      startImpersonation(data.token, data.user);
      toast.success("İzleme (salt-okunur) moduna geçildi");
      navigate("/");
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadAudit = () => api.get("/admin/audit-logs?limit=100").then(({ data }) => setAudit(data.items)).catch(() => {});
  const exportAudit = async () => {
    try {
      const res = await api.get("/admin/audit-logs/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "audit_logs.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const loadBans = () => api.get("/admin/chat-bans").then(({ data }) => setChatBans(data)).catch(() => {});
  useEffect(() => { if (section === "audit") loadAudit(); if (section === "chatbans") loadBans(); }, [section]);
  const unban = async (r) => {
    const reason = window.prompt("Ban kaldırma gerekçesi:", "") || "";
    try { await api.post(`/admin/users/${r.id}/chat-unban`, { reason }); toast.success("Ban kaldırıldı"); loadBans(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const manualBan = async () => {
    const uid = window.prompt("Banlanacak Kullanıcı ID:");
    if (!uid) return;
    const mins = Number(window.prompt("Süre (dakika):", "60")) || 60;
    const reason = window.prompt("Gerekçe:", "") || "";
    try { await api.post(`/admin/users/${uid}/chat-ban`, { minutes: mins, reason }); toast.success("Ban uygulandı"); loadBans(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const editName = async (r) => {
    const raw = window.prompt(`'${r.name}' için yeni ad soyad (admin sınırsız):`, r.name || "");
    if (raw === null || !raw.trim()) return;
    const reason = window.prompt("Müdahale gerekçesi (Destek Talep No / Açıklama):", "");
    if (!reason || !reason.trim()) { toast.error("Müdahale gerekçesi zorunludur"); return; }
    try {
      await api.put(`/admin/users/${r.id}/name`, { name: raw.trim(), reason });
      toast.success("Ad soyad güncellendi");
      api.get("/admin/users").then(({ data }) => setRows(data));
    } catch (e) { toast.error(apiError(e)); }
  };
  const editPhone = async (r) => {
    const raw = window.prompt(`'${r.name}' için telefon numarası:`, r.phone || "");
    if (raw === null || !raw.trim()) return;
    const reason = window.prompt("Müdahale gerekçesi:", "");
    if (!reason || !reason.trim()) { toast.error("Müdahale gerekçesi zorunludur"); return; }
    try {
      await api.put(`/admin/users/${r.id}/phone`, { phone: raw.trim(), reason });
      toast.success("Telefon güncellendi");
      api.get("/admin/users").then(({ data }) => setRows(data));
    } catch (e) { toast.error(apiError(e)); }
  };
  const resetNameEdit = async (r) => {
    try { await api.post(`/admin/users/${r.id}/reset-name-edit`); toast.success("Düzenleme hakkı sıfırlandı"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const loadPkgs = () => api.get("/admin/boost-packages").then(({ data }) => setPkgs(data)).catch(() => {});
  useEffect(() => { if (section === "boost") loadPkgs(); }, [section]);

  const savePkg = async (p) => {
    try {
      await api.put(`/admin/boost-packages/${p.id}`, {
        duration_hours: Number(p.duration_hours), normal_price: Number(p.normal_price),
        sale_price: Number(p.sale_price), active: p.active,
      });
      toast.success("Paket güncellendi"); loadPkgs();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addPkg = async () => {
    try {
      await api.post("/admin/boost-packages", {
        duration_hours: Number(newPkg.duration_hours), normal_price: Number(newPkg.normal_price),
        sale_price: Number(newPkg.sale_price), active: true,
      });
      toast.success("Paket eklendi"); setNewPkg({ duration_hours: "", normal_price: "", sale_price: "" }); loadPkgs();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delPkg = async (id) => {
    try { await api.delete(`/admin/boost-packages/${id}`); toast.success("Paket silindi"); loadPkgs(); }
    catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (section === "images") api.get(`/admin/images?status=${imgStatus}`).then(({ data }) => setImages(data)).catch(() => {});
  }, [section, imgStatus]);

  const reviewImg = async (id, action) => {
    try {
      await api.post(`/admin/images/${id}/${action}`);
      toast.success(action === "approve" ? "Görsel onaylandı" : "Görsel reddedildi / yayından kaldırıldı");
      const { data } = await api.get(`/admin/images?status=${imgStatus}`);
      setImages(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const [blogForm, setBlogForm] = useState({ title: "", slug: "", excerpt: "", content: "", cover: "", category: "", status: "published" });

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  useEffect(() => {
    const map = {
      users: "/admin/users", jobs: "/admin/jobs", transactions: "/admin/transactions",
      moderation: "/admin/moderation-logs", reports: "/admin/reports", blog: "/blog",
    };
    if (map[section]) api.get(map[section]).then(({ data }) => setRows(data)).catch(() => {});
    if (section === "config") api.get("/admin/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, [section]);

  const saveConfig = async () => {
    try {
      await api.put("/admin/config", {
        contact_unlock_price: Number(config.contact_unlock_price),
        launch_bonus_percent: Number(config.launch_bonus_percent),
        wallet_packages: config.wallet_packages,
        global_lock_price_override_enabled: !!config.global_lock_price_override_enabled,
        global_lock_price_amount: Number(config.global_lock_price_amount),
        global_lock_strikethrough_price: Number(config.global_lock_strikethrough_price),
        default_lock_price: Number(config.default_lock_price),
        default_lock_strikethrough_price: Number(config.default_lock_strikethrough_price),
        m2_pricing_tiers: (config.m2_pricing_tiers || []).map((t) => ({
          min: Number(t.min) || 0, max: t.max === null || t.max === "" ? null : Number(t.max),
          amount: Number(t.amount) || 0, strikethrough: Number(t.strikethrough) || 0,
        })),
      });
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const publishBlog = async () => {
    try {
      await api.post("/admin/blog", blogForm);
      toast.success("Yazı yayınlandı");
      setBlogForm({ title: "", slug: "", excerpt: "", content: "", cover: "", category: "", status: "published" });
      api.get("/blog").then(({ data }) => setRows(data));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-60 bg-emerald-950 text-white flex flex-col p-4 shrink-0">
        <Logo onDark />
        <div className="text-xs text-emerald-300/70 mt-1 mb-6">Admin Paneli</div>
        <nav className="space-y-1 flex-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setSection(n.id)} data-testid={`admin-nav-${n.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${section === n.id ? "bg-emerald-500 text-white" : "text-emerald-100 hover:bg-emerald-900"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <Button onClick={() => navigate("/")} variant="ghost" className="text-emerald-100 hover:bg-emerald-900 justify-start"><Home className="h-4 w-4 mr-2" /> Siteye Dön</Button>
      </aside>

      {/* Content */}
      <main className="flex-1 p-8 overflow-auto">
        {section === "dashboard" && (
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 mb-6">Genel Bakış</h1>
            {!stats ? <Loader2 className="h-6 w-6 animate-spin text-emerald-500" /> : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[["Toplam Kullanıcı", stats.users, Users], ["Aktif İlan", stats.active_jobs, Briefcase], ["İletişim Açma", stats.unlocks, ShieldAlert], ["Platform Geliri", tl(stats.revenue), Wallet]].map(([l, v, I], i) => (
                  <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-5">
                    <div className="flex items-center gap-2 text-slate-500 text-sm"><I className="h-4 w-4 text-emerald-500" /> {l}</div>
                    <div className="text-2xl font-extrabold text-slate-900 mt-2">{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {["users", "jobs", "transactions", "moderation", "reports"].includes(section) && (
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 mb-6">{NAV.find((n) => n.id === section)?.label}</h1>
            {(section === "users" || section === "transactions") && (
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad, soyad veya telefon ara..."
                data-testid={`admin-search-${section}`} className="w-full max-w-sm mb-4 h-10 rounded-xl border border-slate-200 px-4 text-sm" />
            )}
            <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>{headers(section).map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
                <tbody>
                  {filterRows(section, rows, search).map((r, i) => <tr key={r.id || i} className="border-t border-slate-100">{cells(section, r, adjustBalance, editName, resetNameEdit, impersonate, editPhone).map((c, j) => <td key={j} className="px-4 py-3 text-slate-700">{c}</td>)}</tr>)}
                </tbody>
              </table>
              {rows.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Kayıt yok.</div>}
            </div>
          </div>
        )}

        {section === "audit" && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-extrabold text-slate-900">İşlem Günlüğü (Audit Logs)</h1>
              <Button onClick={exportAudit} variant="outline" className="rounded-xl" data-testid="audit-export-csv"><FileText className="h-4 w-4 mr-1" /> CSV İndir</Button>
            </div>
            <p className="text-sm text-slate-500 mb-5">Tüm idari müdahaleler değiştirilemez/silinemez olarak kaydedilir.</p>
            <div className="bg-white rounded-2xl border border-slate-200/80 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>{["Tarih", "Admin", "İşlem", "Hedef", "Eski → Yeni", "Gerekçe", "IP"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100" data-testid={`audit-${a.id}`}>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(a.created_at).toLocaleString("tr-TR")}</td>
                      <td className="px-4 py-3 text-slate-700">{a.admin_email}</td>
                      <td className="px-4 py-3"><span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">{a.action_type}</span></td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">{a.target_user_id || "-"}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-xs break-all">{JSON.stringify(a.old_value)} → {JSON.stringify(a.new_value)}</td>
                      <td className="px-4 py-3 text-slate-600">{a.reason || "-"}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{a.ip_address || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {audit.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Kayıt yok.</div>}
            </div>
          </div>
        )}

        {section === "chatbans" && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h1 className="text-2xl font-extrabold text-slate-900">Sohbet Banları</h1>
              <Button onClick={manualBan} className="brand-gradient text-white rounded-xl" data-testid="manual-ban-button"><Ban className="h-4 w-4 mr-1" /> Manuel Ban Ver</Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/80 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left"><tr>{["Ad", "E-posta", "Telefon", "Bitiş", "İşlem"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
                <tbody>
                  {chatBans.map((b) => (
                    <tr key={b.id} className="border-t border-slate-100" data-testid={`ban-${b.id}`}>
                      <td className="px-4 py-3 text-slate-700">{b.name}</td>
                      <td className="px-4 py-3 text-slate-500">{b.email}</td>
                      <td className="px-4 py-3 text-slate-500">{b.phone || "-"}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(b.chat_banned_until).toLocaleString("tr-TR")}</td>
                      <td className="px-4 py-3"><button onClick={() => unban(b)} className="text-xs font-semibold text-emerald-600 hover:underline" data-testid={`unban-${b.id}`}>Banı Kaldır</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {chatBans.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">Şu an aktif ban yok.</div>}
            </div>
          </div>
        )}

        {section === "banned" && (
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 mb-2">Yasaklı Kelimeler</h1>
            <p className="text-sm text-slate-500 mb-5">Bu kelimeler mesajlarda geçtiğinde mesaj "Gizli Bilgi" olarak maskelenir.</p>
            <div className="flex gap-2 max-w-md mb-5">
              <input value={bwInput} onChange={(e) => setBwInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addBw()} placeholder="Yeni kelime" data-testid="banned-input" className="flex-1 h-10 rounded-xl border border-slate-200 px-4 text-sm" />
              <Button onClick={addBw} className="brand-gradient text-white rounded-xl" data-testid="banned-add"><Plus className="h-4 w-4 mr-1" /> Ekle</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {bw.map((b) => (
                <span key={b.id} data-testid={`banned-${b.id}`} className="inline-flex items-center gap-1.5 bg-red-50 text-red-600 text-sm font-semibold px-3 py-1.5 rounded-full">
                  {b.word}<button onClick={() => delBw(b.id)} className="hover:text-red-800"><X className="h-3.5 w-3.5" /></button>
                </span>
              ))}
              {bw.length === 0 && <span className="text-sm text-slate-400">Henüz yasaklı kelime yok.</span>}
            </div>
          </div>
        )}

        {section === "blog" && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 mb-6">Yeni Blog Yazısı</h1>
              <div className="bg-white rounded-2xl border border-slate-200/80 p-6 space-y-3">
                <div><Label>Başlık</Label><Input value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-") })} className="rounded-xl mt-1" data-testid="blog-title-input" /></div>
                <div><Label>Slug</Label><Input value={blogForm.slug} onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })} className="rounded-xl mt-1" /></div>
                <div><Label>Kategori</Label><Input value={blogForm.category} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} className="rounded-xl mt-1" /></div>
                <div><Label>Kapak Görsel URL</Label><Input value={blogForm.cover} onChange={(e) => setBlogForm({ ...blogForm, cover: e.target.value })} className="rounded-xl mt-1" /></div>
                <div><Label>Özet</Label><Textarea value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} className="rounded-xl mt-1" /></div>
                <div><Label>İçerik</Label><Textarea value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} className="rounded-xl mt-1 min-h-32" /></div>
                <Button onClick={publishBlog} className="brand-gradient text-white rounded-xl w-full" data-testid="blog-publish-button">Yayınla</Button>
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 mb-6">Yazılar</h1>
              <div className="space-y-2">{rows.map((p) => <div key={p.id} className="bg-white rounded-2xl border border-slate-200/80 p-4"><div className="font-bold text-slate-900">{p.title}</div><div className="text-xs text-slate-400">{p.category} • {p.status}</div></div>)}</div>
            </div>
          </div>
        )}

        {section === "images" && (
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 mb-4">Görsel Onayları</h1>
            <div className="flex gap-2 mb-5">
              {[["pending", "Bekleyenler"], ["approved", "Onaylananlar"], ["rejected", "Reddedilenler"]].map(([v, l]) => (
                <button key={v} onClick={() => setImgStatus(v)} data-testid={`admin-img-tab-${v}`}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold ${imgStatus === v ? "brand-gradient text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-4">
              {images.length === 0 && <div className="bg-white rounded-2xl border border-slate-200/80 p-10 text-center text-slate-400 text-sm">Bu durumda görsel yok.</div>}
              {images.map((im) => (
                <div key={im.id} className="bg-white rounded-2xl border border-slate-200/80 p-4 grid md:grid-cols-[220px_1fr_180px] gap-4 items-center" data-testid={`admin-img-${im.id}`}>
                  <div className="text-sm">
                    <div className="font-bold text-slate-900">{im.uploader_name}</div>
                    <div className="text-xs text-slate-400 font-mono break-all">{im.user_id}</div>
                    <div className="mt-2"><span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">{im.image_type === "profile" ? "Profil" : im.image_type === "job" ? "İlan" : "Usta Vitrini"}</span></div>
                    <a href={`/usta/${im.user_id}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 font-semibold mt-2 inline-block">Profili Gör →</a>
                    <div className="text-[11px] text-slate-400 mt-1">{new Date(im.created_at).toLocaleString("tr-TR")}</div>
                  </div>
                  <div className="flex justify-center">
                    <img src={`${BE}${im.url}`} alt="" className="rounded-xl max-h-[480px] w-auto object-contain border" style={{ minHeight: "220px" }} />
                  </div>
                  <div className="flex md:flex-col gap-2">
                    {im.status !== "approved" && (
                      <button onClick={() => reviewImg(im.id, "approve")} data-testid={`admin-img-approve-${im.id}`} className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-500 text-white rounded-xl h-10 font-semibold hover:bg-emerald-600"><Check className="h-4 w-4" /> Onayla</button>
                    )}
                    <button onClick={() => reviewImg(im.id, "reject")} data-testid={`admin-img-reject-${im.id}`} className="flex-1 flex items-center justify-center gap-1.5 bg-red-500 text-white rounded-xl h-10 font-semibold hover:bg-red-600"><X className="h-4 w-4" /> {im.status === "approved" ? "Yayından Kaldır" : "Reddet"}</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {section === "boost" && (
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 mb-2">Boost & Vitrin Ayarları</h1>
            <p className="text-sm text-slate-500 mb-6">Öne çıkarma paketlerini yönetin. İndirim yüzdesi ve kazanç otomatik hesaplanır.</p>
            <div className="space-y-3 max-w-3xl">
              {pkgs.map((p, i) => (
                <div key={p.id} className="bg-white rounded-2xl border border-slate-200/80 p-4 grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_auto_auto] gap-3 items-end" data-testid={`admin-pkg-${p.id}`}>
                  <div><Label className="text-xs">Süre (Saat)</Label><Input type="number" value={p.duration_hours} onChange={(e) => setPkgs((prev) => prev.map((x, j) => j === i ? { ...x, duration_hours: e.target.value } : x))} className="rounded-xl mt-1" /></div>
                  <div><Label className="text-xs">Normal Fiyat</Label><Input type="number" value={p.normal_price} onChange={(e) => setPkgs((prev) => prev.map((x, j) => j === i ? { ...x, normal_price: e.target.value } : x))} className="rounded-xl mt-1" /></div>
                  <div><Label className="text-xs">İndirimli Fiyat</Label><Input type="number" value={p.sale_price} onChange={(e) => setPkgs((prev) => prev.map((x, j) => j === i ? { ...x, sale_price: e.target.value } : x))} className="rounded-xl mt-1" /></div>
                  <div className="text-center">
                    <div className="text-[11px] text-slate-400">Kazanç</div>
                    <div className="text-sm font-bold text-emerald-600">%{p.discount_percent} · {tl(p.savings)}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => savePkg(p)} className="brand-gradient text-white rounded-xl h-10" data-testid={`admin-pkg-save-${p.id}`}>Kaydet</Button>
                    <button onClick={() => delPkg(p.id)} className="h-10 w-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100" data-testid={`admin-pkg-del-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-4 grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
                <div><Label className="text-xs">Süre (Saat)</Label><Input type="number" value={newPkg.duration_hours} onChange={(e) => setNewPkg({ ...newPkg, duration_hours: e.target.value })} className="rounded-xl mt-1 bg-white" data-testid="admin-pkg-new-hours" /></div>
                <div><Label className="text-xs">Normal Fiyat</Label><Input type="number" value={newPkg.normal_price} onChange={(e) => setNewPkg({ ...newPkg, normal_price: e.target.value })} className="rounded-xl mt-1 bg-white" data-testid="admin-pkg-new-normal" /></div>
                <div><Label className="text-xs">İndirimli Fiyat</Label><Input type="number" value={newPkg.sale_price} onChange={(e) => setNewPkg({ ...newPkg, sale_price: e.target.value })} className="rounded-xl mt-1 bg-white" data-testid="admin-pkg-new-sale" /></div>
                <Button onClick={addPkg} className="brand-gradient text-white rounded-xl h-10" data-testid="admin-pkg-add"><Plus className="h-4 w-4 mr-1" /> Ekle</Button>
              </div>
            </div>
          </div>
        )}

        {section === "config" && config && (          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 mb-6">Fiyat & Ayarlar</h1>
            <div className="bg-white rounded-2xl border border-slate-200/80 p-6 max-w-lg space-y-4">
              <div><Label>İletişim Açma Ücreti (TL)</Label><Input type="number" value={config.contact_unlock_price} onChange={(e) => setConfig({ ...config, contact_unlock_price: e.target.value })} className="rounded-xl mt-1" data-testid="config-unlock-price" /></div>
              <div><Label>Açılış Bonus Oranı (%)</Label><Input type="number" value={config.launch_bonus_percent} onChange={(e) => setConfig({ ...config, launch_bonus_percent: e.target.value })} className="rounded-xl mt-1" data-testid="config-bonus-percent" /></div>
              <div><Label>Cüzdan Paketleri (virgülle)</Label><Input value={(config.wallet_packages || []).join(", ")} onChange={(e) => setConfig({ ...config, wallet_packages: e.target.value.split(",").map((x) => Number(x.trim())).filter(Boolean) })} className="rounded-xl mt-1" /></div>
              <Button onClick={saveConfig} className="brand-gradient text-white rounded-xl" data-testid="config-save-button">Kaydet</Button>
            </div>

            <LockPricingCard config={config} setConfig={setConfig} onSave={saveConfig} />

            <DangerZoneCard config={config} />
          </div>
        )}
      </main>
    </div>
  );
}

function filterRows(section, rows, search) {
  if (!search || !(section === "users" || section === "transactions")) return rows;
  const q = search.toLowerCase();
  return rows.filter((r) => `${r.name || r.user_name || ""} ${r.phone || r.user_phone || ""}`.toLowerCase().includes(q));
}
function headers(section) {
  return {
    users: ["Ad", "Telefon", "Gerçek Bakiye", "Bonus Bakiye", "Durum", "İşlem"],
    jobs: ["Başlık", "Kategori", "Şehir", "Teklif", "Durum"],
    transactions: ["Kişi", "Telefon", "Tip", "Tutar", "Açıklama", "Tarih"],
    moderation: ["Tip", "Risk", "Aksiyon", "Tarih"],
    reports: ["Hedef", "Sebep", "Durum", "Tarih"],
  }[section] || [];
}
function cells(section, r, adjustBalance, editName, resetNameEdit, impersonate, editPhone) {
  const d = (x) => x ? new Date(x).toLocaleDateString("tr-TR") : "-";
  return {
    users: [
      <span key="nm" className="flex items-center gap-2">{r.name}
        <button onClick={() => editName(r)} className="text-slate-400 hover:text-emerald-600" title="Adı düzenle" data-testid={`edit-name-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
        {(r.name_change_count || 0) >= 1 && <button onClick={() => resetNameEdit(r)} className="text-[10px] text-amber-600 hover:underline" title="Düzenleme hakkını sıfırla" data-testid={`reset-name-${r.id}`}>sıfırla</button>}
      </span>,
      <span key="ph" className="flex items-center gap-2">{r.phone || "-"}
        <button onClick={() => editPhone(r)} className="text-slate-400 hover:text-emerald-600" title="Telefonu düzenle" data-testid={`edit-phone-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
      </span>,
      <button key="rb" onClick={() => adjustBalance(r.id, "real")} className="font-semibold text-slate-600 hover:underline" data-testid={`adjust-real-${r.id}`}>{tl(r.real_balance || 0)}</button>,
      <button key="bb" onClick={() => adjustBalance(r.id, "bonus")} className="font-semibold text-emerald-600 hover:underline" data-testid={`adjust-bonus-${r.id}`}>{tl(r.bonus_balance || 0)}</button>,
      r.status,
      <button key="imp" onClick={() => impersonate(r)} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:underline" data-testid={`impersonate-${r.id}`}><Eye className="h-3.5 w-3.5" /> Gözünden Gör</button>,
    ],
    jobs: [r.title, r.category_name, r.city, r.offer_count, r.status],
    transactions: [r.user_name || "-", r.user_phone || "-", r.type, tl(r.amount), r.description, d(r.created_at)],
    moderation: [r.detected_type, r.risk_score, r.action, d(r.created_at)],
    reports: [r.target_type, r.reason, r.status, d(r.created_at)],
  }[section] || [];
}

function LockPricingCard({ config, setConfig, onSave }) {
  const override = !!config.global_lock_price_override_enabled;
  const tiers = config.m2_pricing_tiers || [];
  const setTier = (i, key, val) => setConfig({ ...config, m2_pricing_tiers: tiers.map((t, j) => j === i ? { ...t, [key]: val } : t) });
  const addTier = () => setConfig({ ...config, m2_pricing_tiers: [...tiers, { min: 0, max: null, amount: 50, strikethrough: 100 }] });
  const delTier = (i) => setConfig({ ...config, m2_pricing_tiers: tiers.filter((_, j) => j !== i) });
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-6 max-w-2xl mt-6" data-testid="lock-pricing-card">
      <h2 className="text-lg font-extrabold text-slate-900">İletişim Kilidi Fiyatlandırma Yönetimi</h2>
      <div className="flex items-center justify-between mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
        <div>
          <div className="font-bold text-slate-900 text-sm">Tüm Fiyatları Sabitle (Lansman / Kampanya Modu)</div>
          <div className="text-xs text-slate-500">Açıkken tüm kilit açmalar aşağıdaki sabit fiyattan tahsil edilir.</div>
        </div>
        <button onClick={() => setConfig({ ...config, global_lock_price_override_enabled: !override })} data-testid="lock-override-toggle"
          className={`relative h-6 w-11 rounded-full transition-colors ${override ? "bg-emerald-500" : "bg-slate-300"}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${override ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>

      {/* Global fixed price (applies when campaign mode is ON) */}
      <div className={`grid grid-cols-2 gap-3 mt-4 ${override ? "" : "opacity-50"}`}>
        <div><Label className="text-xs">Sabit Tahsil (indirimli) TL</Label><Input type="number" value={config.global_lock_price_amount} onChange={(e) => setConfig({ ...config, global_lock_price_amount: e.target.value })} className="rounded-xl mt-1" data-testid="lock-global-amount" /></div>
        <div><Label className="text-xs">Sabit Üstü çizili liste TL</Label><Input type="number" value={config.global_lock_strikethrough_price} onChange={(e) => setConfig({ ...config, global_lock_strikethrough_price: e.target.value })} className="rounded-xl mt-1" data-testid="lock-global-strike" /></div>
      </div>

      {/* Tiered m² pricing table (always visible) */}
      <div className="mt-6 pt-5 border-t border-slate-100 space-y-3">
        <div>
          <div className="text-base font-extrabold text-slate-900">Kademeli Metrekare (m²) Fiyatlandırma Listesi</div>
          <div className="text-xs text-slate-500 mt-0.5">Kampanya modu kapalıyken ilanlarda belirtilen m² ölçüsüne göre bu tablodaki ücretler tahsil edilir.</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Varsayılan tahsil TL (m² yoksa)</Label><Input type="number" value={config.default_lock_price} onChange={(e) => setConfig({ ...config, default_lock_price: e.target.value })} className="rounded-xl mt-1" data-testid="lock-default-amount" /></div>
          <div><Label className="text-xs">Varsayılan liste TL</Label><Input type="number" value={config.default_lock_strikethrough_price} onChange={(e) => setConfig({ ...config, default_lock_strikethrough_price: e.target.value })} className="rounded-xl mt-1" data-testid="lock-default-strike" /></div>
        </div>
        <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 text-[11px] font-semibold text-slate-400 px-1 pt-2">
          <span>Min m²</span><span>Max m² (boş = +)</span><span>Tahsil Edilecek (TL)</span><span>Üstü Çizili Liste (TL)</span><span>İşlem</span>
        </div>
        {tiers.map((t, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`lock-tier-${i}`}>
            <div><Label className="text-[10px] sm:hidden">Min m²</Label><Input type="number" value={t.min} onChange={(e) => setTier(i, "min", e.target.value)} className="rounded-lg mt-1 h-9" data-testid={`lock-tier-min-${i}`} /></div>
            <div><Label className="text-[10px] sm:hidden">Max m² (boş=+)</Label><Input type="number" placeholder="+" value={t.max ?? ""} onChange={(e) => setTier(i, "max", e.target.value === "" ? null : e.target.value)} className="rounded-lg mt-1 h-9" data-testid={`lock-tier-max-${i}`} /></div>
            <div><Label className="text-[10px] sm:hidden">Tahsil TL</Label><Input type="number" value={t.amount} onChange={(e) => setTier(i, "amount", e.target.value)} className="rounded-lg mt-1 h-9" data-testid={`lock-tier-amount-${i}`} /></div>
            <div><Label className="text-[10px] sm:hidden">Liste TL</Label><Input type="number" value={t.strikethrough} onChange={(e) => setTier(i, "strikethrough", e.target.value)} className="rounded-lg mt-1 h-9" data-testid={`lock-tier-strike-${i}`} /></div>
            <button onClick={() => delTier(i)} className="h-9 w-9 rounded-lg bg-red-50 text-red-500 flex items-center justify-center" data-testid={`lock-tier-del-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <Button onClick={addTier} variant="outline" className="rounded-xl border-emerald-200 text-emerald-700" data-testid="lock-tier-add"><Plus className="h-4 w-4 mr-1" /> Yeni m² Aralığı Ekle</Button>
      </div>
      <Button onClick={onSave} className="brand-gradient text-white rounded-xl mt-5" data-testid="lock-pricing-save">Fiyatlandırmayı Kaydet</Button>
    </div>
  );
}






function DangerZoneCard({ config }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [used, setUsed] = useState(false);

  // Self-destruct: once the wipe has been used, the tool disappears permanently
  if (used || config?.db_wipe_used) return null;

  const wipe = async () => {
    if (confirmText !== "SIL") { toast.error('Onaylamak için kutuya SIL yazın'); return; }
    if (!window.confirm("Bu işlem GERİ ALINAMAZ. Admin hesabı ve sistem ayarları hariç TÜM veriler (kullanıcılar, ilanlar, mesajlar, cüzdanlar, işlemler) silinecek. Ayrıca bu araç kullanımdan sonra KALICI olarak kaldırılacak. Devam edilsin mi?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/wipe-database", { confirm: "SIL", reason: "Admin panelinden veritabanı temizliği (tek kullanımlık)" });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Veritabanı temizlendi. Toplam ${total} kayıt silindi. Bu araç artık kaldırıldı.`);
      setUsed(true);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border-2 border-red-200 p-6 max-w-lg mt-6" data-testid="danger-zone-card">
      <h2 className="text-lg font-extrabold text-red-600 flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Tehlikeli Bölge</h2>
      <p className="text-sm text-slate-500 mt-1">
        Admin hesabınız ve sistem ayarları (kategoriler, boost paketleri, blog) hariç
        <b> tüm veriyi</b> kalıcı olarak siler. <b>Tek kullanımlıktır</b> — bir kez kullanıldığında bu araç otomatik olarak kaldırılır. Bu işlem geri alınamaz.
      </p>
      <div className="mt-4 space-y-3">
        <div>
          <Label>Onaylamak için <b>SIL</b> yazın</Label>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="SIL" className="rounded-xl mt-1" data-testid="wipe-confirm-input" />
        </div>
        <Button onClick={wipe} disabled={busy || confirmText !== "SIL"} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-11 font-semibold disabled:opacity-50" data-testid="wipe-database-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-2" /> Veritabanını Temizle</>}
        </Button>
      </div>
    </div>
  );
}
